import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Copy, Download, Plus, Shield, Pencil, Trash2, Eye, Upload } from 'lucide-react';

import { PageLayout } from '@/components/Layout/PageLayout';
import AccessDenied from '@/components/Common/AccessDenied';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { ConfirmationModal } from '@/components/Common/ConfirmationModal';
import { useIsSuperAdmin } from '@/stores/authStore';
import { useProfiles } from '@/hooks/useProfiles';
import { profileService } from '@/services/profileService';
import type { Profile, ProfileImportPayload } from '@/types/profiles';
import { notify } from '@/lib/notify';

const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  // Pydantic 422: surface the first validation message (e.g. an unknown resource in an imported file).
  if (Array.isArray(detail) && typeof detail[0]?.msg === 'string') return detail[0].msg;
  return err?.message ?? 'Error';
};

const downloadJson = (data: unknown, filename: string) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const ProfilesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();
  const { profiles, isLoading, error, remove } = useProfiles();

  const [deleteTarget, setDeleteTarget] = useState<Profile | null>(null);
  const [reassignTo, setReassignTo] = useState<string>('');
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const sortedProfiles = useMemo(
    () => [...profiles].sort((a, b) => Number(b.is_system) - Number(a.is_system) || a.name.localeCompare(b.name)),
    [profiles]
  );

  const canManage = (_p: Profile): boolean => isSuperAdmin;
  const canCreate = isSuperAdmin;

  if (!isSuperAdmin) {
    return <AccessDenied />;
  }

  const handleDuplicate = async (p: Profile) => {
    setDuplicatingId(p.id);
    try {
      const copy = await profileService.duplicate(p.id, {
        name: t('profiles.duplicate.copyOf', { name: p.name }),
      });
      notify.success(t('profiles.duplicate.done', 'Profile duplicated'));
      navigate(`/admin/profiles/${copy.id}/overview`);
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleExport = async (p: Profile) => {
    setExportingId(p.id);
    try {
      downloadJson(await profileService.exportJson(p.id), `profile-${p.key}.json`);
    } catch (e) {
      notify.error(errorMessage(e));
    } finally {
      setExportingId(null);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again still fires onChange.
    e.target.value = '';
    if (!file) return;
    let payload: ProfileImportPayload;
    try {
      payload = JSON.parse(await file.text());
    } catch {
      notify.error(t('profiles.import.invalidJson', 'This file is not valid JSON.'));
      return;
    }
    if (payload?.format !== 'etabli.profile') {
      notify.error(t('profiles.import.notAProfile', 'This file is not an exported profile.'));
      return;
    }
    setImporting(true);
    try {
      const created = await profileService.importJson(payload);
      notify.success(t('profiles.import.done', 'Profile imported'));
      navigate(`/admin/profiles/${created.id}/overview`);
    } catch (err) {
      notify.error(errorMessage(err));
    } finally {
      setImporting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id, reassignTo || null);
      notify.success(t('profiles.deleted', 'Profile deleted'));
      setDeleteTarget(null);
      setReassignTo('');
    } catch (e) {
      notify.error(errorMessage(e));
    }
  };

  return (
    <PageLayout
      icon={<Shield className="h-6 w-6" />}
      title={t('profiles.title', 'Profiles')}
      subtitle={t(
        'profiles.description',
        'Define which pages and actions each user can access.'
      )}
      headerRight={
        <div className="flex items-center gap-2">
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={handleImportFile}
          />
          <Button
            variant="outline"
            disabled={importing}
            onClick={() => importInputRef.current?.click()}
            title={t('profiles.import.hint', 'Create a profile from an exported JSON file')}
          >
            <Upload className="h-4 w-4 mr-1" />
            {t('profiles.import.action', 'Import')}
          </Button>
          <Button onClick={() => navigate('/admin/profiles/new')}>
            <Plus className="h-4 w-4 mr-1" />
            {t('profiles.new', 'New profile')}
          </Button>
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>{t('profiles.listTitle', 'All profiles')}</CardTitle>
          <CardDescription>
            {t(
              'profiles.listDescription',
              'System profiles (god, admin, metier, developer) can be edited by super-admins but cannot be deleted.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-muted-foreground text-sm">{t('common.loading', 'Loading…')}</div>
          ) : error ? (
            <div className="text-destructive text-sm">{error.message}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40 text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('profiles.columns.name', 'Name')}</th>
                    <th className="px-3 py-2 font-medium">{t('profiles.columns.key', 'Key')}</th>
                    <th className="px-3 py-2 font-medium">{t('profiles.columns.type', 'Type')}</th>
                    <th className="px-3 py-2 font-medium">{t('profiles.columns.description', 'Description')}</th>
                    <th className="px-3 py-2 font-medium text-right">{t('profiles.columns.actions', 'Actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedProfiles.map((p) => (
                    <tr key={p.id} className="border-t border-border/60">
                      <td className="px-3 py-2 font-medium">{p.name}</td>
                      <td className="px-3 py-2 font-mono text-xs">{p.key}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 flex-wrap">
                          {p.is_system ? (
                            <span className="inline-block text-xs bg-muted px-2 py-0.5 rounded">
                              {t('profiles.system', 'system')}
                            </span>
                          ) : p.organization_id === null ? (
                            <span className="inline-block text-xs bg-muted px-2 py-0.5 rounded">
                              {t('profiles.global', 'global')}
                            </span>
                          ) : (
                            <span className="inline-block text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                              {t('profiles.organization', 'organization')}
                            </span>
                          )}
                          {p.default_for_role && (
                            <span className="inline-block text-xs text-muted-foreground border border-border/60 px-2 py-0.5 rounded">
                              {t('profiles.defaultFor', 'default: {{role}}', { role: p.default_for_role })}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground truncate max-w-[320px]">
                        {p.description ?? '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          {canManage(p) ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/admin/profiles/${p.id}/overview`)}
                              title={t('profiles.edit', 'Edit')}
                              aria-label={t('profiles.edit', 'Edit')}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/admin/profiles/${p.id}/overview`)}
                              title={`${t('profiles.view', 'View')} — ${t('profiles.readOnlyGlobal', 'Global profiles are read-only for organization admins.')}`}
                              aria-label={t('profiles.view', 'View')}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          )}
                          {canCreate && (
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={duplicatingId !== null}
                              onClick={() => handleDuplicate(p)}
                              title={t('profiles.duplicate.action', 'Duplicate')}
                              aria-label={t('profiles.duplicate.action', 'Duplicate')}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={exportingId !== null}
                            onClick={() => handleExport(p)}
                            title={t('profiles.export.action', 'Export as JSON')}
                            aria-label={t('profiles.export.action', 'Export as JSON')}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={p.is_system || !canManage(p)}
                            onClick={() => setDeleteTarget(p)}
                            title={
                              canManage(p)
                                ? t('profiles.delete', 'Delete')
                                : t('profiles.readOnlyGlobal', 'Global profiles are read-only for organization admins.')
                            }
                            aria-label={t('profiles.delete', 'Delete')}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete confirmation */}
      <ConfirmationModal
        open={!!deleteTarget}
        title={t('profiles.deleteTitle', 'Delete profile?')}
        message={t(
          'profiles.deleteBody',
          'Users currently assigned to this profile must be reassigned first.'
        )}
        confirmText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={confirmDelete}
        onCancel={() => {
          setDeleteTarget(null);
          setReassignTo('');
        }}
      >
        <div className="space-y-2">
          <label className="text-sm font-medium block" htmlFor="profile-reassign-to">
            {t('profiles.reassignTo', 'Reassign users to')}
          </label>
          <select
            id="profile-reassign-to"
            className="w-full border border-input rounded-md bg-background px-3 py-2 text-sm"
            value={reassignTo}
            onChange={(e) => setReassignTo(e.target.value)}
          >
            <option value="">{t('profiles.reassignNone', '— none (fail if any users) —')}</option>
            {profiles
              .filter((p) => p.id !== deleteTarget?.id)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.key})
                </option>
              ))}
          </select>
        </div>
      </ConfirmationModal>
    </PageLayout>
  );
};

export default ProfilesPage;
